// Import model
import { Estimate } from '../models/Estimate';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { Discount } from '../models/Discount';
import { EstimateSection } from '../models/EstimateSection';
import { LineItem } from '../models/LineItem';
import { TaxLiability } from '../models/TaxLiability';
import { TaxRates } from '../models/TaxRates';

// Import dependency serializers (for complex types)
import { DiscountSerializer } from './DiscountSerializer';
import { EstimateSectionSerializer } from './EstimateSectionSerializer';
import { LineItemSerializer } from './LineItemSerializer';
import { TaxLiabilitySerializer } from './TaxLiabilitySerializer';
import { TaxRatesSerializer } from './TaxRatesSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Estimate - handles JSON serialization and deserialization with type validation
 */
export class EstimateSerializer {
  /**
   * Deserializes a JSON string into a Estimate instance
   */
  static deserialize(json: string): Estimate {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Estimate instance
   */
  static fromObject(obj: any): Estimate {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Estimate');
    }


    const instance: Estimate = {
      projectExternalId: obj['projectExternalId'] as string | undefined,
      projectExternalIdName: obj['projectExternalId|Name'] as string | undefined,
      roomSelectionIds: obj['roomSelectionIds'] ? (obj['roomSelectionIds'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      createdByUserId: obj['createdByUserId'] as string | undefined,
      createdByUserIdName: obj['createdByUserId|Name'] as string | undefined,
      lineItems: obj['lineItems'] ? (obj['lineItems'] as any[]).map((item: any) =>
        LineItemSerializer.fromObject(item)
      ) : [] as any,
      sessionKey: obj['sessionKey'] as string | undefined,
      hasWarnings: obj['hasWarnings'] as boolean | undefined,
      totalCost: obj['totalCost'] as number | undefined,
      totalPrice: obj['totalPrice'] as number | undefined,
      totalTax: obj['totalTax'] as number | undefined,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      flowId: obj['FlowId'] as string | undefined,
      flowIdName: obj['FlowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      taxLiabilities: obj['taxLiabilities'] ? (obj['taxLiabilities'] as any[]).map((item: any) =>
        TaxLiabilitySerializer.fromObject(item)
      ) : [] as any,
      estimateNumber: obj['estimateNumber'] as string | undefined,
      summary: obj['summary'] as string | undefined,
      version: obj['version'] as number | undefined,
      taxRates: obj['taxRates'] ? TaxRatesSerializer.fromObject(obj['taxRates']) : undefined as any,
      discounts: obj['discounts'] ? (obj['discounts'] as any[]).map((item: any) =>
        DiscountSerializer.fromObject(item)
      ) : [] as any,
      blendedMargin: obj['blendedMargin'] as number | undefined,
      discountPrice: obj['discountPrice'] as number | undefined,
      discountTax: obj['discountTax'] as number | undefined,
      grandTotal: obj['grandTotal'] as number | undefined,
      sections: obj['Sections'] ? (obj['Sections'] as any[]).map((item: any) =>
        EstimateSectionSerializer.fromObject(item)
      ) : [] as any,
      grandTax: obj['grandTax'] as number | undefined,
      relatedObjects: obj['relatedObjects'] as string | undefined,
      docusealSubmissionId: obj['docusealSubmissionId'] as string | undefined,
      docusealSubmissionIdName: obj['docusealSubmissionId|Name'] as string | undefined,
      sfExternalId: obj['sfExternalId'] as string | undefined,
      sfExternalIdName: obj['sfExternalId|Name'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Estimate instances
   */
  static deserializeArray(json: string): Estimate[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Estimate instance to a JSON string
   */
  static serialize(instance: Estimate): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Estimate instance to a plain object
   */
  static toObject(instance: Estimate): any {
    return {
      'projectExternalId': instance.projectExternalId,
      'projectExternalId|Name': instance.projectExternalIdName,
      'roomSelectionIds': instance.roomSelectionIds?.map((item: any) =>
        item
      ),
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'createdByUserId': instance.createdByUserId,
      'createdByUserId|Name': instance.createdByUserIdName,
      'lineItems': instance.lineItems?.map((item: any) =>
        LineItemSerializer.toObject(item)
      ),
      'sessionKey': instance.sessionKey,
      'hasWarnings': instance.hasWarnings,
      'totalCost': instance.totalCost,
      'totalPrice': instance.totalPrice,
      'totalTax': instance.totalTax,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'FlowId': instance.flowId,
      'FlowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'isActive': instance.isActive,
      'taxLiabilities': instance.taxLiabilities?.map((item: any) =>
        TaxLiabilitySerializer.toObject(item)
      ),
      'estimateNumber': instance.estimateNumber,
      'summary': instance.summary,
      'version': instance.version,
      'taxRates': instance.taxRates ? TaxRatesSerializer.toObject(instance.taxRates) : undefined,
      'discounts': instance.discounts?.map((item: any) =>
        DiscountSerializer.toObject(item)
      ),
      'blendedMargin': instance.blendedMargin,
      'discountPrice': instance.discountPrice,
      'discountTax': instance.discountTax,
      'grandTotal': instance.grandTotal,
      'Sections': instance.sections?.map((item: any) =>
        EstimateSectionSerializer.toObject(item)
      ),
      'grandTax': instance.grandTax,
      'relatedObjects': instance.relatedObjects,
      'docusealSubmissionId': instance.docusealSubmissionId,
      'docusealSubmissionId|Name': instance.docusealSubmissionIdName,
      'sfExternalId': instance.sfExternalId,
      'sfExternalId|Name': instance.sfExternalIdName,
    };
  }

  /**
   * Serializes an array of Estimate instances to a JSON string
   */
  static serializeArray(instances: Estimate[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
